import React, { useState, useEffect } from "react";
import styles from "./PromotionalPrompt.module.scss";
import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import {
  SecondaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { SaleTagIcon } from "@/components/__shared__/SaleTagIcon/SaleTagIcon";
import { HorizontalRule } from "@/components/HorizontalRule/HorizontalRule";
import TermsAndConditionsAccordion from "@/components/__shared__/TermsAndConditionsAccordion/TermsAndConditionsAccordion";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import {
  getPromoTitleFromDescription,
  stripHTML,
  getDescriptionStartingWithClaim,
} from "@/components/SpecialOffers/utils";

type Props = {
  message: string;
  promoCode: string;
  terms: string | undefined;
  isBlackOverride: boolean;
  promoDescription?: string;
};

export const PromotionalPromptUI = ({
  message,
  promoCode,
  terms,
  isBlackOverride,
  promoDescription,
}: Props) => {
  const [copyButtonIsClicked, setCopyButtonIsClicked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(promoCode?.length > 0);

  const promotionTitle = getPromoTitleFromDescription(
    message,
    promoDescription || "",
  );
  const cleanPromotionTitle = stripHTML(promotionTitle);
  const promoDescriptionContent = getDescriptionStartingWithClaim(
    promoDescription || "",
    promotionTitle,
  );

  useEffect(() => {
    if (isModalOpen) dispatchMonetateQEvent("PROMOTION_PROMPT_MODAL_OPENED");
  }, [isModalOpen]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const copyPromoCode = () => {
    navigator.clipboard.writeText(promoCode);
    setCopyButtonIsClicked(true);
    dispatchMonetateQEvent("COPY_PROMO_BUTTON_CLICKED");
  };

  const openBasket = () => {
    window.open("/basket", "_blank");
    dispatchMonetateQEvent("VIEW_BASKET_BUTTON_CLICKED");
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={closeModal}
      title="The items in your basket qualify for a special offer"
      rootId="page-root"
      footer={false}
    >
      <div>
        <p className={styles.message}>Here&apos;s how to get it:</p>
        <HorizontalRule />
        <div className={styles.promoTitleContainer}>
          <SaleTagIcon type="small" isBlackOverride={isBlackOverride} />
          <p className={styles.promoTitle}>{cleanPromotionTitle}</p>
        </div>
        {promoDescriptionContent && (
          <div
            className={styles.promoDescription}
            dangerouslySetInnerHTML={{
              __html: promoDescriptionContent,
            }}
          />
        )}
        {terms && (
          <div className={styles.termsContainer}>
            <TermsAndConditionsAccordion
              terms={terms}
              isGiftWithPurchaseEnabled={true}
            />
          </div>
        )}
        <HorizontalRule />
        <div className={styles.modalFooter}>
          <UnderlinedButton
            className={styles.viewBasketButton}
            onClick={openBasket}
            data-testid="promotional-prompt-basket-button"
          >
            View basket
          </UnderlinedButton>
          <SecondaryButton
            className={styles.copyPromoButton}
            disabled={copyButtonIsClicked}
            id={`copyPromoButton${promoCode}`}
            onClick={() => copyPromoCode()}
          >
            {copyButtonIsClicked ? "Copied" : "Copy promo code"}
          </SecondaryButton>
        </div>
      </div>
    </Modal>
  );
};
